import { Injectable } from '@angular/core';
import Compressor from 'compressorjs';
import { saveAs } from 'file-saver';
import { format, addMonths, isBefore, isWeekend, addDays } from 'date-fns';


@Injectable({
   providedIn: 'root',
})
export class Utils {
   // private base64Encoding: BufferEncoding = 'base64';
   // private asciiEncoding: BufferEncoding = 'ascii';
   private readonly MAX_STRING_LENGTH = 4000000; // ~4MB de texto

   constructor() {}

   blobToFile(theBlob: Blob, fileName: string): File {
      const b: any = theBlob;
      //A Blob() is almost a File() - it's just missing the two properties below which we will add
      b.lastModifiedDate = new Date();
      b.name = fileName;

      //Cast to a File() type
      return <File>theBlob;
   }

   dataUrlToFile(dataURL: string, filename?: string) {
      const splited = dataURL.split(',');
      const blobBin = this.base64ToUtf8(splited[1]);
      // separate out the mime component
      const mimeString = splited[0].split(':')[1].split(';')[0];
      const array = [];

      for (let i = 0; i < blobBin.length; i++)
         array.push(blobBin.charCodeAt(i));

      const blob = new Blob([new Uint8Array(array)], { type: mimeString });

      return this.blobToFile(blob, filename ? filename : 'signature');
   }

   encodeBase64(text: string) {
      return this.utf8ToBase64(text);
   }

   decodeBase64(encrypt: string) {
      return this.base64ToUtf8(encrypt);
   }

   // Codificación UTF-8 a Base64 mejorada
   utf8ToBase64(str: string, secureFallback: boolean = false): string {
      // Validación de entrada
      if (str === null || str === undefined) {
         console.warn('utf8ToBase64: Entrada nula o indefinida');
         return '';
      }

      if (typeof str !== 'string') {
         console.warn('utf8ToBase64: La entrada debe ser una cadena de texto');
         try {
            str = String(str);
         } catch (e) {
            console.error('utf8ToBase64: No se pudo convertir la entrada a string', e);
            return '';
         }
      }

      // Validación de límite para prevenir DoS
      if (str.length > this.MAX_STRING_LENGTH) {
         console.warn(`utf8ToBase64: Entrada excede el tamaño máximo permitido (${this.MAX_STRING_LENGTH})`);
         return '';
      }

      // Detección de caracteres problemáticos
      if (/[\uD800-\uDFFF]/.test(str)) {
         console.warn('utf8ToBase64: La cadena contiene pares sustitutos UTF-16 que podrían causar problemas');
         // Continuar, pero con advertencia registrada
      }

      try {
         const encoder = new TextEncoder();
         const bytes = encoder.encode(str);
         const binary = Array.from(bytes).map(b => String.fromCharCode(b)).join('');
         return window.btoa(binary);
      } catch (error) {
         console.error('utf8ToBase64: Error en la codificación', error);

         // Método alternativo en caso de fallo, respetando secureFallback
         if (!secureFallback) {
            try {
               return this.fallbackUtf8ToBase64(str);
            } catch (fallbackError) {
               console.error('utf8ToBase64: Falló el método alternativo', fallbackError);
               return '';
            }
         } else {
            console.warn('utf8ToBase64: Modo seguro activado, no se utilizará método alternativo');
            return '';
         }
      }
   }

   // Decodificación Base64 a UTF-8 mejorada
   base64ToUtf8(base64: string, secureFallback: boolean = false): string {
      // Validación de entrada
      if (base64 === null || base64 === undefined) {
         console.warn('base64ToUtf8: Entrada nula o indefinida');
         return '';
      }

      if (typeof base64 !== 'string') {
         console.warn('base64ToUtf8: La entrada debe ser una cadena de texto');
         try {
            base64 = String(base64);
         } catch (e) {
            console.error('base64ToUtf8: No se pudo convertir la entrada a string', e);
            return '';
         }
      }

      // Validación de límite para prevenir DoS
      if (base64.length > this.MAX_STRING_LENGTH) {
         console.warn(`base64ToUtf8: Entrada excede el tamaño máximo permitido (${this.MAX_STRING_LENGTH})`);
         return '';
      }

      // Validar formato Base64
      const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
      if (!base64Regex.test(base64)) {
         console.warn('base64ToUtf8: La cadena no parece ser Base64 válido');
         // Continuar de todos modos, pero con advertencia
      }

      try {
         const binary = window.atob(base64);
         const bytes = Uint8Array.from(binary, c => c.charCodeAt(0));
         const decoder = new TextDecoder('utf-8', { fatal: false });

         // Comprobar si el resultado podría contener caracteres problemáticos
         const result = decoder.decode(bytes);
         if (/[\uD800-\uDFFF]/.test(result)) {
            console.warn('base64ToUtf8: El resultado contiene pares sustitutos UTF-16 que podrían causar problemas');
         }

         return result;
      } catch (error) {
         console.error('base64ToUtf8: Error en la decodificación', error);

         // Método alternativo en caso de fallo, respetando secureFallback
         if (!secureFallback) {
            try {
               return this.fallbackBase64ToUtf8(base64);
            } catch (fallbackError) {
               console.error('base64ToUtf8: Falló el método alternativo', fallbackError);
               return '';
            }
         } else {
            console.warn('base64ToUtf8: Modo seguro activado, no se utilizará método alternativo');
            return '';
         }
      }
   }

   // Métodos de respaldo (fallback)
   private fallbackUtf8ToBase64(str: string): string {
      // Método alternativo usando encodeURIComponent
      try {
         // Reemplazar caracteres que causan problemas en btoa
         return window.btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g,
            (match, p1) => String.fromCharCode(parseInt(p1, 16))));
      } catch (e) {
         throw e; // Propagar el error si también falla
      }
   }

   private fallbackBase64ToUtf8(base64: string): string {
      // Método alternativo usando decodeURIComponent
      try {
         return decodeURIComponent(Array.prototype.map.call(window.atob(base64),
            c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
      } catch (e) {
         throw e; // Propagar el error si también falla
      }
   }


   /** Guardar archivos para plataformas Web */
   async saveFile(
      buffer: ArrayBuffer,
      filename: string,
      type = 'application/pdf'
   ) {
      const blob = new Blob([buffer], { type });
      saveAs(blob, filename);
   }

   processImg(img: File) {
      return new Compressor(img, {
         quality: 0.8,
         maxWidth: 1500,

         // The compression process is asynchronous,
         // which means you have to access the `result` in the `success` hook function.
         success(result) {
            return result;
         },
         error(err) {
            console.log(err.message);
         },
      });
   }

   // Get month name from numeric value, use date pipe
   getMonthName(month: number) {
      const date = new Date();
      date.setMonth(month - 1);
      return date.toLocaleString('es-ES', { month: 'long' });
   }

   calculateNextDistributionDate(
      lastDistDate: string,
      devCapitalization: string
   ): string {
      if (!lastDistDate) {
         return 'Proyecto activo';
      }

      const baseDate = new Date(lastDistDate);
      const currentDate = new Date();

      const calculateNextDate = (months: number): Date => {
         let nextDate = addMonths(baseDate, months);
         while (isBefore(nextDate, currentDate)) {
            nextDate = addMonths(nextDate, months);
         }
         // Fijamos siempre el día 15 para la fecha de distribución
         return new Date(nextDate.getFullYear(), nextDate.getMonth(), 15);
      };

      const nextDate = this.getNextDistributionDate(
         baseDate,
         currentDate,
         devCapitalization,
         calculateNextDate
      );

      // Formateamos la fecha antes de retornarla
      return format(nextDate, 'yyyy-MM-dd');
   }

   private getNextDistributionDate(
      baseDate: Date,
      currentDate: Date,
      devCapitalization: string,
      calculateNextDate: (months: number) => Date
   ): Date {
      let nextDate: Date;

      switch (devCapitalization) {
         case 'Mensual':
            nextDate = calculateNextDate(1);
            break;
         case 'Bimestral':
            nextDate = calculateNextDate(2);
            break;
         case 'Trimestral':
            nextDate = calculateNextDate(3);
            break;
         case 'Venta o en 5 meses':
            nextDate = calculateNextDate(5);
            break;
         default:
            throw new Error(
               `Unknown capitalization type: ${devCapitalization}`
            );
      }

      // Ajustamos la fecha si cae en fin de semana
      return this.adjustForWeekend(nextDate);
   }

   private adjustForWeekend(date: Date): Date {
      let adjustedDate = new Date(date);
      if (isWeekend(adjustedDate)) {
         while (isWeekend(adjustedDate)) {
            adjustedDate = addDays(adjustedDate, 1);
         }
      }
      return adjustedDate;
   }

   /**
    * Limpia un string removiendo caracteres que puedan causar conflictos en URLs o nombres de archivo.
    *
    * @param input - El texto a limpiar.
    * @returns Un texto sin comas, puntos u otros símbolos conflictivos.
    */
   sanitizeString(input: string): string {
      return input
         .replace(/[^\w\s-]/g, '')
         .replace(/\s+/g, '_')
         .trim();
   }

   monthNameByMonth(month: number): string {
      const months = [
         'Enero',
         'Febrero',
         'Marzo',
         'Abril',
         'Mayo',
         'Junio',
         'Julio',
         'Agosto',
         'Septiembre',
         'Octubre',
         'Noviembre',
         'Diciembre',
      ];
      return months[month];
   }
}
